//===----------------------------------------------------------------------===//
//
//  Copyright 2024-2026 Trovares Inc. dba Rocketgraph.  All rights reserved.
//
//===----------------------------------------------------------------------===//

import { useCallback, useEffect, useSyncExternalStore } from 'react';
import { router } from '@/routes';
import { authService, sessionActivity } from '@/services';
import { useAuth } from '@/hooks/useAuth';
import { useConfig } from '@/hooks/useConfig';
import SessionTimeoutModal from './SessionTimeoutModal';
import { ERoutesRoot } from '@/routes/RoutesEnum';

// User-activity signals that keep the session alive. Movement and scroll are
// included (not just clicks/keys) so ordinary use — panning a graph, reading a
// long list — counts; the keepalive they trigger is throttled in the
// controller, so the event volume is harmless.
const ACTIVITY_EVENTS =
  ['keydown', 'mousedown', 'mousemove', 'scroll', 'wheel', 'touchstart'];

// Drives the inactivity-timeout UX while a user is logged in: starts/stops the
// activity controller with the session, feeds it user input (the ACTIVITY_EVENTS
// above — clicks, keys, and movement/scroll), and renders the warning modal.
// When the controller reports expiry it runs the same logout + redirect path a
// 401 would, so an abandoned tab ends up back at login.
const SessionTimeout = () => {
  const { activeUser, logout } = useAuth();
  const { appConfig } = useConfig();
  const { status, warningActive, expiresAt } = useSyncExternalStore(
    sessionActivity.subscribe, sessionActivity.getSnapshot);

  useEffect(() => {
    sessionActivity.configure(appConfig?.session_ttl);
  }, [appConfig?.session_ttl]);

  const endSession = useCallback(async (broadcast: boolean) => {
    try {
      await logout(broadcast);
    } catch (err) {
      // logout() rethrows (e.g. the /api/logout POST 401s because the session
      // already expired — the very case that triggers this path).  Swallow it:
      // this is automatic cleanup and the redirect below logs the user out
      // regardless; an unhandled rejection here would just be noise.
      console.warn('Session termination cleanup error:', err);
    } finally {
      router.navigate(ERoutesRoot.Login);
    }
  }, [logout]);

  useEffect(() => {
    if (!activeUser) return;
    sessionActivity.start();
    const onInteract = () => sessionActivity.recordInteraction();
    // Typed as AddEventListenerOptions (a subtype of removeEventListener's
    // EventListenerOptions) so the same object is valid for both calls —
    // `passive` is an add-only field that EventListenerOptions doesn't allow.
    const listenerOptions: AddEventListenerOptions = { passive: true };
    ACTIVITY_EVENTS.forEach(evt =>
      window.addEventListener(evt, onInteract, listenerOptions));
    return () => {
      ACTIVITY_EVENTS.forEach(evt =>
        window.removeEventListener(evt, onInteract, listenerOptions));
      sessionActivity.stop();
    };
  }, [activeUser]);

  useEffect(() => {
    if (status === 'expired') {
      // Flag it so the login page shows "Session expired" — a client-driven
      // idle logout never hits the 401 path that would otherwise set this.
      // No broadcast: the shared expiry clock already lands every tab here.
      authService.markSessionExpired();
      endSession(false);
    } else if (status === 'loggedout') {
      // Another tab logged out; tear down without re-broadcasting (and without
      // the expired notice — this was a logout, not a timeout).
      endSession(false);
    }
  }, [status, endSession]);

  if (!activeUser)
    return null;

  return (
    <SessionTimeoutModal
      open={warningActive}
      expiresAt={expiresAt}
      onStay={() => sessionActivity.extend()}
      onLogout={() => endSession(true)}
    />
  );
};

export default SessionTimeout;
